import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import { useAuth } from "@workos-inc/authkit-react";

export const T = {
  bg: "#F2F7FD",
  panel: "#FFFFFF",
  panel2: "#E5EEF9",
  ink: "#1C1917",
  ink2: "#57534E",
  ink3: "#A8A29E",
  line: "#D0DEF0",
  sky: "#7AB4F4",
  skyDark: "#3B82C8",
  skySoft: "#D2E4FC",
  green: "#2A7A4B",
  greenSoft: "#E8F3EC",
  amber: "#B87333",
  amberSoft: "#FCEFD9",
  plum: "#8E4585",
  plumSoft: "#F3E6F2",
  red: "#C0392B",
  redSoft: "#FDECEA",
};

export const sans = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
export const mono = '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace';

export type Status = "todo" | "in_progress" | "review" | "done" | "archived";

export type TicketRow = {
  id: string;
  slug: string;
  project_id: string | null;
  project_name: string | null;
  goal_id: string | null;
  goal_title: string | null;
  agent: string | null;
  title: string;
  status: Status;
  current_step: string | null;
  log: string | null;
  created_at: string;
  updated_at: string;
};

export type ProjectRow = {
  id: string;
  slug: string;
  name: string;
  mission: string | null;
  status: "active" | "paused" | "done" | "archived";
  ticket_count: number;
  goal_count: number;
};

export type AgentRow = {
  name: string;
  display_name: string;
  color: string | null;
  current_ticket_id: string | null;
  current_ticket_title: string | null;
  current_ticket_status: string | null;
  in_flight_count: number;
};

export type Memory = {
  id: string;
  agent: string | null;
  type: string;
  title: string | null;
  body: string;
  created_at: string;
};

export type KbPage = {
  page_slug: string;
  title: string;
  updated_at: string;
};

type TicketDetail = TicketRow & {
  tier: string | null;
  problem: string | null;
  vision: string | null;
  goal: string | null;
  iscs: { id: string; text: string; done: boolean }[];
};

const COLUMN_ORDER: Status[] = [
  "todo",
  "in_progress",
  "review",
  "done",
  "archived",
];

const STATUS_LABEL: Record<Status, string> = {
  todo: "To do",
  in_progress: "In progress",
  review: "Review",
  done: "Done",
  archived: "Archived",
};

const COLUMN_HEADER_BG: Record<Status, string> = {
  todo: T.panel2,
  in_progress: T.skySoft,
  review: T.amberSoft,
  done: T.greenSoft,
  archived: T.line,
};

const COLUMN_HEADER_FG: Record<Status, string> = {
  todo: T.ink2,
  in_progress: T.skyDark,
  review: T.amber,
  done: T.green,
  archived: T.ink3,
};

const CARD_ACCENT: Record<Status, string> = {
  todo: T.ink3,
  in_progress: T.skyDark,
  review: T.amber,
  done: T.green,
  archived: T.line,
};

const PROJECT_STATUS_TONE: Record<
  ProjectRow["status"],
  { bg: string; color: string; label: string }
> = {
  active: { bg: T.skySoft, color: T.skyDark, label: "Active" },
  paused: { bg: T.amberSoft, color: T.amber, label: "Paused" },
  done: { bg: T.greenSoft, color: T.green, label: "Done" },
  archived: { bg: T.panel2, color: T.ink3, label: "Archived" },
};

export const AGENT_COLOR: Record<string, string> = {
  jarvis: T.skyDark,
  atlas: "#C7763A",
  ben: T.green,
  nova: T.plum,
  emma: "#D6336C",
  iris: T.sky,
};

function lastLogLine(log: string | null): string | null {
  if (!log) return null;
  const lines = log
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return lines.length === 0 ? null : lines[lines.length - 1];
}

// ── Atoms ──────────────────────────────────────────────────────────────────

function StatusDot({ color }: { color: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: color,
        marginRight: 6,
      }}
    />
  );
}

function PanelHeader({
  left,
  right,
}: {
  left: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div
      style={{
        padding: "9px 13px",
        borderBottom: `1px solid ${T.line}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontFamily: sans,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.13em",
        textTransform: "uppercase",
        color: T.ink2,
      }}
    >
      <span>{left}</span>
      {right && <span style={{ color: T.ink3, fontWeight: 600 }}>{right}</span>}
    </div>
  );
}

// ── Header strip ───────────────────────────────────────────────────────────

export function HeaderStrip({ subtitle }: { subtitle: string }) {
  const { user } = useAuth();
  const fullName = [user?.firstName, user?.lastName]
    .filter((v): v is string => Boolean(v))
    .join(" ");
  const displayName = fullName || user?.email?.split("@")[0] || "User";
  const initial = displayName.charAt(0).toUpperCase();
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
  const timeStr = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingBottom: 8,
        borderBottom: `1px solid ${T.line}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: T.skySoft,
            color: T.skyDark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: sans,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {initial}
        </div>
        <div>
          <div
            style={{
              fontFamily: sans,
              fontSize: 14,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: "-0.01em",
            }}
          >
            {displayName} · MyJarvis OS
          </div>
          <div
            style={{
              fontFamily: sans,
              fontSize: 11,
              color: T.ink2,
              marginTop: 1,
            }}
          >
            <StatusDot color={T.green} />
            {dateStr} · {timeStr}
          </div>
        </div>
      </div>
      <span
        style={{
          fontFamily: sans,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.13em",
          textTransform: "uppercase",
          color: T.skyDark,
          background: T.skySoft,
          padding: "4px 10px",
          borderRadius: 999,
        }}
      >
        {subtitle}
      </span>
    </div>
  );
}

// ── Goals strip ────────────────────────────────────────────────────────────

type Goal = {
  id: string;
  slug: string;
  title: string;
  project_name: string | null;
  status: "active" | "paused" | "done" | "archived";
  ticket_count: number;
};

const GOAL_STATUS_TONE: Record<
  Goal["status"],
  { bg: string; color: string; label: string }
> = {
  active: { bg: T.skySoft, color: T.skyDark, label: "Active" },
  paused: { bg: T.amberSoft, color: T.amber, label: "Paused" },
  done: { bg: T.greenSoft, color: T.green, label: "Done" },
  archived: { bg: T.panel2, color: T.ink3, label: "Archived" },
};

function GoalsStrip() {
  const api = useApi();
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchOnce() {
      try {
        const res = await api("/api/goals");
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as Goal[];
        if (!cancelled) {
          setGoals(
            data
              .filter((g) => g.status !== "archived")
              .sort((a, b) => {
                // Active first, then paused, then done; within group by ticket count
                const order = { active: 0, paused: 1, done: 2, archived: 3 };
                if (order[a.status] !== order[b.status]) {
                  return order[a.status] - order[b.status];
                }
                return b.ticket_count - a.ticket_count;
              }),
          );
        }
      } catch {
        if (!cancelled) setGoals([]);
      }
    }
    void fetchOnce();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      <PanelHeader
        left="Goals · click to open"
        right={
          goals !== null
            ? `${goals.filter((g) => g.status === "active").length} active · ${goals.length} total`
            : "…"
        }
      />
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: 10,
          overflowX: "auto",
          minHeight: 86,
        }}
      >
        {goals === null && (
          <div
            style={{
              fontFamily: sans,
              fontSize: 12,
              color: T.ink3,
              padding: "6px 4px",
            }}
          >
            Loading…
          </div>
        )}
        {goals && goals.length === 0 && (
          <div
            style={{
              fontFamily: sans,
              fontSize: 12,
              color: T.ink3,
              fontStyle: "italic",
              padding: "6px 4px",
            }}
          >
            No goals yet.
          </div>
        )}
        {goals?.map((g) => {
          const tone = GOAL_STATUS_TONE[g.status];
          return (
            <div
              key={g.id}
              onClick={() => navigate("/goals-list")}
              style={{
                flex: "0 0 auto",
                width: 240,
                background: T.bg,
                border: `1px solid ${T.line}`,
                borderLeft: `3px solid ${tone.color}`,
                borderRadius: 8,
                padding: "10px 12px",
                cursor: "pointer",
                transition: "background 0.15s ease, border-color 0.15s ease",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = T.panel2;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = T.bg;
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    background: tone.bg,
                    color: tone.color,
                    padding: "2px 7px",
                    borderRadius: 999,
                    fontFamily: sans,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {tone.label}
                </span>
                <span
                  style={{
                    fontFamily: mono,
                    fontSize: 9.5,
                    color: T.ink3,
                    marginLeft: "auto",
                  }}
                >
                  {g.ticket_count} ticket{g.ticket_count === 1 ? "" : "s"}
                </span>
              </div>
              <div
                style={{
                  fontFamily: sans,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: T.ink,
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
                title={g.title}
              >
                {g.title}
              </div>
              {g.project_name && (
                <div
                  style={{
                    fontFamily: sans,
                    fontSize: 10.5,
                    color: T.ink3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={g.project_name}
                >
                  {g.project_name}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Projects list ──────────────────────────────────────────────────────────

function ProjectsList() {
  const api = useApi();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/projects");
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as ProjectRow[];
        if (!cancelled) setProjects(data);
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCount = projects?.filter((p) => p.status === "active").length ?? 0;

  return (
    <div
      style={{
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <PanelHeader
        left="Projects · click to open"
        right={projects ? `${activeCount} active` : "…"}
      />
      <div style={{ flex: 1, overflow: "auto" }}>
        {projects === null && (
          <div
            style={{
              padding: 14,
              fontFamily: sans,
              fontSize: 12,
              color: T.ink3,
            }}
          >
            Loading…
          </div>
        )}
        {projects && projects.length === 0 && (
          <div
            style={{
              padding: 14,
              fontFamily: sans,
              fontSize: 12,
              color: T.ink3,
              fontStyle: "italic",
            }}
          >
            No projects yet.
          </div>
        )}
        {projects?.map((p, i) => {
          const tone = PROJECT_STATUS_TONE[p.status];
          return (
            <div
              key={p.id}
              onClick={() => navigate(`/projects-list`)}
              style={{
                padding: "11px 13px",
                borderTop: i > 0 ? `1px solid ${T.line}` : "none",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = T.panel2)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontFamily: mono,
                    fontSize: 10,
                    color: T.skyDark,
                    fontWeight: 700,
                  }}
                >
                  {p.slug}
                </span>
                <span
                  style={{
                    background: tone.bg,
                    color: tone.color,
                    padding: "2px 7px",
                    borderRadius: 999,
                    fontFamily: sans,
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {tone.label}
                </span>
              </div>
              <div
                style={{
                  fontFamily: sans,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: T.ink,
                  marginBottom: 3,
                }}
              >
                {p.name}
              </div>
              {p.mission && (
                <div
                  style={{
                    fontFamily: sans,
                    fontSize: 11,
                    color: T.ink2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={p.mission}
                >
                  {p.mission}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  fontFamily: mono,
                  fontSize: 10,
                  color: T.ink3,
                  marginTop: 4,
                }}
              >
                <span>{p.ticket_count} tickets</span>
                <span>·</span>
                <span>{p.goal_count} goals</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tickets Kanban ─────────────────────────────────────────────────────────

function TicketsKanban({
  onOpen,
}: {
  onOpen: (slug: string) => void;
}) {
  const api = useApi();
  const [rows, setRows] = useState<TicketRow[] | null>(null);
  const pendingMoves = useRef<Map<string, Status>>(new Map());
  const isDraggingRef = useRef(false);
  const [dragging, setDragging] = useState<{
    id: string;
    from: Status;
  } | null>(null);
  const [dragOver, setDragOver] = useState<Status | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchOnce() {
      if (isDraggingRef.current) return;
      try {
        const res = await api("/api/tickets");
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as TicketRow[];
        if (cancelled) return;
        const merged = data.map((r) => {
          const pending = pendingMoves.current.get(r.id);
          return pending ? { ...r, status: pending } : r;
        });
        setRows(merged);
      } catch {
        // ignore
      }
    }
    void fetchOnce();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function moveTicket(id: string, from: Status, to: Status) {
    if (from === to || !rows) return;
    const ticket = rows.find((r) => r.id === id);
    if (!ticket) return;

    pendingMoves.current.set(id, to);
    setRows((cur) =>
      cur ? cur.map((r) => (r.id === id ? { ...r, status: to } : r)) : cur,
    );

    try {
      const res = await api(`/api/tickets/${ticket.slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: to }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      pendingMoves.current.delete(id);
    } catch {
      pendingMoves.current.delete(id);
      setRows((cur) =>
        cur
          ? cur.map((r) => (r.id === id ? { ...r, status: from } : r))
          : cur,
      );
    }
  }

  const grouped: Record<Status, TicketRow[]> = {
    todo: [],
    in_progress: [],
    review: [],
    done: [],
    archived: [],
  };
  if (rows) for (const r of rows) grouped[r.status]?.push(r);

  const totalActive =
    (rows ?? []).filter((r) => r.status !== "archived" && r.status !== "done")
      .length;

  return (
    <div
      style={{
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderRadius: 8,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <PanelHeader
        left="Tickets · drag to move · click for detail"
        right={rows ? `${totalActive} active` : "…"}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${COLUMN_ORDER.length}, minmax(0, 1fr))`,
          gap: 8,
          padding: 10,
          flex: 1,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {COLUMN_ORDER.map((status) => {
          const tickets = grouped[status];
          const isOver =
            dragOver === status && dragging && dragging.from !== status;
          return (
            <div
              key={status}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragging && dragOver !== status) setDragOver(status);
              }}
              onDragLeave={() => {
                if (dragOver === status) setDragOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging) {
                  void moveTicket(dragging.id, dragging.from, status);
                  setDragging(null);
                  setDragOver(null);
                  isDraggingRef.current = false;
                }
              }}
              style={{
                background: isOver ? T.skySoft : T.bg,
                border: `1px solid ${isOver ? T.skyDark : T.line}`,
                borderRadius: 8,
                padding: 8,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                minWidth: 0,
                transition:
                  "background 0.18s ease, border-color 0.18s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  paddingBottom: 6,
                  marginBottom: 6,
                  borderBottom: `1px solid ${T.line}`,
                }}
              >
                <span
                  style={{
                    fontFamily: sans,
                    fontSize: 11,
                    fontWeight: 700,
                    color: COLUMN_HEADER_FG[status],
                    background: COLUMN_HEADER_BG[status],
                    padding: "2px 7px",
                    borderRadius: 4,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {STATUS_LABEL[status]}
                </span>
                <span
                  style={{
                    fontFamily: mono,
                    fontSize: 10,
                    color: T.ink3,
                  }}
                >
                  {tickets.length}
                </span>
              </div>
              <div style={{ flex: 1, overflow: "auto" }}>
                {tickets.map((t) => {
                  const isThisDragging = dragging?.id === t.id;
                  const tail = lastLogLine(t.log);
                  const agentColor = t.agent
                    ? AGENT_COLOR[t.agent] ?? T.ink2
                    : T.ink3;
                  return (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={() => {
                        setDragging({ id: t.id, from: status });
                        isDraggingRef.current = true;
                      }}
                      onDragEnd={() => {
                        setDragging(null);
                        setDragOver(null);
                        isDraggingRef.current = false;
                      }}
                      onClick={() => {
                        if (!dragging) onOpen(t.slug);
                      }}
                      style={{
                        background: T.panel,
                        border: `1px solid ${T.line}`,
                        borderLeft: `3px solid ${CARD_ACCENT[t.status]}`,
                        borderRadius: 6,
                        padding: "8px 10px",
                        marginBottom: 6,
                        cursor: isThisDragging ? "grabbing" : "grab",
                        boxShadow: isThisDragging
                          ? "0 8px 20px rgba(28,25,23,0.18)"
                          : "0 1px 1px rgba(28,25,23,0.03)",
                        opacity: isThisDragging ? 0.55 : 1,
                        transition:
                          "box-shadow 0.15s ease, opacity 0.15s ease",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: sans,
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: T.ink,
                          lineHeight: 1.3,
                          marginBottom: 4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        title={t.title}
                      >
                        {t.title}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          flexWrap: "wrap",
                          fontFamily: mono,
                          fontSize: 9.5,
                          color: T.ink3,
                        }}
                      >
                        <span
                          style={{
                            color: T.skyDark,
                            background: T.skySoft,
                            padding: "1px 5px",
                            borderRadius: 3,
                            fontWeight: 600,
                          }}
                        >
                          {t.slug}
                        </span>
                        {t.agent && (
                          <span
                            style={{
                              color: agentColor,
                              fontWeight: 600,
                              textTransform: "lowercase",
                            }}
                          >
                            @{t.agent}
                          </span>
                        )}
                        {t.current_step && (
                          <span style={{ color: T.ink3 }}>{t.current_step}</span>
                        )}
                      </div>
                      {tail && (
                        <div
                          style={{
                            marginTop: 4,
                            fontFamily: sans,
                            fontSize: 10,
                            color: T.ink3,
                            fontStyle: "italic",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={tail}
                        >
                          {tail}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Ticket modal ───────────────────────────────────────────────────────────

export function TicketModal({
  slug,
  onClose,
}: {
  slug: string;
  onClose: () => void;
}) {
  const api = useApi();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [files, setFiles] = useState<KbPage[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api(`/api/tickets/${slug}`);
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const data = (await res.json()) as TicketDetail;
        if (cancelled) return;
        setTicket(data);

        // Linked files heuristic — KB pages mentioning the ticket slug or
        // sharing the project slug prefix.
        try {
          const kbRes = await api(`/api/kb`);
          if (kbRes.ok) {
            const kb = (await kbRes.json()) as KbPage[];
            const slugLower = slug.toLowerCase();
            const projectSlug =
              data.project_id && data.project_name
                ? data.project_name.toLowerCase().replace(/\s+/g, "-")
                : null;
            const matched = kb.filter((p) => {
              const ps = p.page_slug.toLowerCase();
              if (ps.includes(slugLower)) return true;
              if (projectSlug && ps.includes(`/${projectSlug}/`)) return true;
              return false;
            });
            if (!cancelled) setFiles(matched.slice(0, 8));
          }
        } catch {
          // ignore — files are optional
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const statusTone =
    ticket && {
      todo: { bg: T.panel2, color: T.ink2, label: "To do" },
      in_progress: { bg: T.skySoft, color: T.skyDark, label: "In progress" },
      review: { bg: T.amberSoft, color: T.amber, label: "Review" },
      done: { bg: T.greenSoft, color: T.green, label: "Done" },
      archived: { bg: T.line, color: T.ink3, label: "Archived" },
    }[ticket.status];

  const agentColor = ticket?.agent ? AGENT_COLOR[ticket.agent] ?? T.ink2 : T.ink3;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,25,23,0.45)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.panel,
          borderRadius: 14,
          border: `1px solid ${T.line}`,
          maxWidth: 760,
          width: "100%",
          maxHeight: "85vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(28,25,23,0.18)",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${T.line}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 14,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 6,
                flexWrap: "wrap",
              }}
            >
              <code
                style={{
                  fontFamily: mono,
                  fontSize: 11,
                  color: T.skyDark,
                  background: T.skySoft,
                  padding: "2px 7px",
                  borderRadius: 4,
                  fontWeight: 700,
                }}
              >
                {slug}
              </code>
              {statusTone && (
                <span
                  style={{
                    background: statusTone.bg,
                    color: statusTone.color,
                    padding: "3px 9px",
                    borderRadius: 999,
                    fontFamily: sans,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {statusTone.label}
                </span>
              )}
              {ticket?.tier && (
                <span
                  style={{
                    background: T.plumSoft,
                    color: T.plum,
                    padding: "3px 9px",
                    borderRadius: 999,
                    fontFamily: mono,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {ticket.tier}
                </span>
              )}
              {ticket?.agent && (
                <span
                  style={{
                    color: agentColor,
                    fontFamily: sans,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "lowercase",
                  }}
                >
                  @{ticket.agent}
                </span>
              )}
            </div>
            <div
              style={{
                fontFamily: sans,
                fontSize: 18,
                fontWeight: 700,
                color: T.ink,
                letterSpacing: "-0.01em",
                lineHeight: 1.3,
              }}
            >
              {ticket?.title ?? "Loading…"}
            </div>
            {(ticket?.project_name || ticket?.goal_title) && (
              <div
                style={{
                  fontFamily: sans,
                  fontSize: 12,
                  color: T.ink2,
                  marginTop: 6,
                }}
              >
                {ticket.project_name && (
                  <>
                    <span style={{ color: T.ink3 }}>Project:</span>{" "}
                    {ticket.project_name}
                  </>
                )}
                {ticket.project_name && ticket.goal_title && " · "}
                {ticket.goal_title && (
                  <>
                    <span style={{ color: T.ink3 }}>Goal:</span>{" "}
                    {ticket.goal_title}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: T.ink3,
              fontSize: 24,
              lineHeight: 1,
              padding: 4,
              flexShrink: 0,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            display: "grid",
            gap: 16,
          }}
        >
          {error && (
            <div
              style={{
                background: T.redSoft,
                color: T.red,
                padding: "10px 12px",
                borderRadius: 8,
                fontFamily: sans,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}

          {ticket && (ticket.problem || ticket.vision) && (
            <div style={{ display: "grid", gap: 12 }}>
              {ticket.problem && (
                <Section
                  label="Problem"
                  body={ticket.problem}
                  tone={{ bg: T.redSoft, color: T.red }}
                />
              )}
              {ticket.vision && (
                <Section
                  label="Vision"
                  body={ticket.vision}
                  tone={{ bg: T.skySoft, color: T.skyDark }}
                />
              )}
            </div>
          )}

          {ticket?.iscs && ticket.iscs.length > 0 && (
            <div>
              <SectionLabel>Implementation criteria</SectionLabel>
              <div style={{ display: "grid", gap: 4 }}>
                {ticket.iscs.slice(0, 8).map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                      padding: "6px 10px",
                      background: c.done ? T.greenSoft : T.panel2,
                      borderRadius: 6,
                      fontFamily: sans,
                      fontSize: 12,
                      color: T.ink,
                    }}
                  >
                    <span
                      style={{
                        color: c.done ? T.green : T.ink3,
                        flexShrink: 0,
                      }}
                    >
                      {c.done ? "✓" : "○"}
                    </span>
                    <span
                      style={{
                        textDecoration: c.done ? "line-through" : "none",
                        color: c.done ? T.ink2 : T.ink,
                      }}
                    >
                      {c.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ticket?.log && (
            <div>
              <SectionLabel>Log</SectionLabel>
              <pre
                style={{
                  background: T.panel2,
                  border: `1px solid ${T.line}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  fontFamily: mono,
                  fontSize: 11,
                  color: T.ink,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                  maxHeight: 200,
                  overflowY: "auto",
                }}
              >
                {ticket.log}
              </pre>
            </div>
          )}

          <div>
            <SectionLabel>Linked files</SectionLabel>
            {files.length === 0 ? (
              <div
                style={{
                  fontFamily: sans,
                  fontSize: 12,
                  color: T.ink3,
                  fontStyle: "italic",
                  padding: "6px 0",
                }}
              >
                No KB pages linked to this ticket yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {files.map((f) => (
                  <a
                    key={f.page_slug}
                    href={`/${f.page_slug}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "8px 12px",
                      border: `1px solid ${T.line}`,
                      borderRadius: 6,
                      background: T.panel,
                      textDecoration: "none",
                      color: T.ink,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: sans,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: T.ink,
                      }}
                    >
                      {f.title}
                    </span>
                    <span
                      style={{
                        fontFamily: mono,
                        fontSize: 10,
                        color: T.skyDark,
                      }}
                    >
                      /{f.page_slug}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <a
              href={`/tickets/${slug}`}
              style={{
                flex: 1,
                background: T.skyDark,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "11px 14px",
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "none",
                textAlign: "center",
              }}
            >
              Open full ticket page →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontFamily: sans,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: T.ink2,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Section({
  label,
  body,
  tone,
}: {
  label: string;
  body: string;
  tone: { bg: string; color: string };
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: sans,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: tone.color,
          background: tone.bg,
          padding: "3px 9px",
          borderRadius: 999,
          display: "inline-block",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: sans,
          fontSize: 13,
          color: T.ink,
          lineHeight: 1.55,
          background: T.panel2,
          border: `1px solid ${T.line}`,
          borderRadius: 8,
          padding: "10px 14px",
          whiteSpace: "pre-wrap",
        }}
      >
        {body}
      </div>
    </div>
  );
}

// ── OperatorPage layout ────────────────────────────────────────────────────

export function OperatorPage({
  variantLabel,
}: {
  variantLabel: string;
}) {
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  return (
    <div
      style={{
        background: T.bg,
        padding: "16px",
        height: "100%",
        fontFamily: sans,
        color: T.ink,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <HeaderStrip subtitle={variantLabel} />

      <GoalsStrip />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)",
          gap: 12,
          flex: 1,
          minHeight: 0,
        }}
      >
        <ProjectsList />
        <TicketsKanban onOpen={setOpenSlug} />
      </div>

      {openSlug && (
        <TicketModal slug={openSlug} onClose={() => setOpenSlug(null)} />
      )}
    </div>
  );
}
